import React from 'react';
import {
  Show,
  SimpleShowLayout,
  TextField,
  DateField,
  FunctionField,
  TopToolbar,
  EditButton,
  DeleteButton,
  useRecordContext,
} from 'react-admin';
import { Box, Chip, Typography, Card, CardContent, IconButton, Tooltip } from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { partnerMarketingAssetsFields } from '../partnerMarketingAssets.model';

const ShowActions = () => (
  <TopToolbar>
    <EditButton />
    <DeleteButton mutationMode="pessimistic" />
  </TopToolbar>
);

const FileSizeField = () => {
  const record = useRecordContext();
  if (!record || record.file_size_bytes === undefined) {
    return <Typography>-</Typography>;
  }
  const bytes = record.file_size_bytes;
  if (bytes === 0) return <Typography>0 Bytes</Typography>;
  const k = 1024;
  const dm = 2;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return <Typography>{parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} {sizes[i]}</Typography>;
};

const PublicUrlField = () => {
  const record = useRecordContext();

  const handleCopy = () => {
    if (record?.public_url) {
      navigator.clipboard.writeText(record.public_url);
    }
  };

  if (!record || !record.public_url) {
    return <Typography>-</Typography>;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography
        variant="body2"
        sx={{
          fontFamily: 'monospace',
          wordBreak: 'break-all',
          bgcolor: 'grey.100',
          p: 1,
          borderRadius: 1,
          flex: 1,
        }}
      >
        {record.public_url}
      </Typography>
      <Tooltip title="Copy URL">
        <IconButton size="small" onClick={handleCopy}>
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Open in new tab">
        <IconButton
          size="small"
          onClick={() => window.open(record.public_url, '_blank')}
        >
          <LaunchIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const PreviewCard = () => {
  const record = useRecordContext();

  if (!record) return null;

  const isImage = record.content_type?.startsWith('image/');
  const isPdf = record.content_type === 'application/pdf';

  return (
    <Card sx={{ mt: 2, mb: 2 }}>
      <CardContent>
        <Typography variant="subtitle2" gutterBottom>
          Preview
        </Typography>
        {isImage && (
          <Box
            component="img"
            src={record.public_url}
            alt={record.display_name || record.original_filename}
            sx={{
              maxWidth: '100%',
              maxHeight: 400,
              objectFit: 'contain',
              border: '1px solid',
              borderColor: 'grey.300',
              borderRadius: 1,
            }}
          />
        )}
        {isPdf && (
          <Box sx={{ textAlign: 'center', py: 4, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              PDF Preview not available.
            </Typography>
            <Chip
              label="Open PDF"
              icon={<LaunchIcon />}
              onClick={() => window.open(record.public_url, '_blank')}
              clickable
              color="primary"
              sx={{ mt: 1 }}
            />
          </Box>
        )}
        {!isImage && !isPdf && (
          <Typography variant="body2" color="text.secondary">
            Preview not available for this file type.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const AssetShow = () => {
  return (
    <Show title="Partner Marketing Asset" actions={<ShowActions />}>
      <SimpleShowLayout>
        <TextField source={partnerMarketingAssetsFields.id} label="ID" />
        <TextField source={partnerMarketingAssetsFields.displayName} label="Display Name" />
        <TextField source={partnerMarketingAssetsFields.originalFilename} label="Original Filename" />
        <TextField source={partnerMarketingAssetsFields.folderPath} label="Folder Path" />
        <TextField source={partnerMarketingAssetsFields.fileType} label="File Type" />
        <FunctionField label="File Size" render={() => <FileSizeField />} />
        <TextField source={partnerMarketingAssetsFields.contentType} label="Content Type" />
        <FunctionField label="Public URL" render={() => <PublicUrlField />} />
        <TextField source={partnerMarketingAssetsFields.uploadedByUserId} label="Uploaded By User ID" />
        <DateField source={partnerMarketingAssetsFields.createdAt} label="Created At" showTime />
        <DateField source={partnerMarketingAssetsFields.updatedAt} label="Updated At" showTime />
        <PreviewCard />
      </SimpleShowLayout>
    </Show>
  );
};

export default AssetShow;
